<script setup lang="ts">
/**
 * Подзадачи — макет 13:460: заголовок со счётчиком «2 / 3», полоса прогресса
 * и строки с ключом, названием и точкой статуса.
 *
 * Прогресс считаем по категории колонки, а не по названию: «Готово» в одном
 * проекте зовётся «Выкачено», и сравнивать строки значило бы считать
 * неправильно у всех, кто переименовал колонку.
 *
 * Новая подзадача заводится строкой прямо здесь: уходить ради неё в модалку
 * создания и заново выбирать проект, тип и родителя — три лишних шага на
 * действие, которое делают по ходу разбора задачи.
 */
import { AppButton } from '@vinprop/design-system'
import { Plus } from 'lucide-vue-next'
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { createIssue } from '@/features/issues/api'
import IssueTypeIcon from '@/features/issues/components/IssueTypeIcon.vue'

import { listChildren } from '../links'
import { useIssueStore } from '../store'

import type { IssueRow } from '@/features/issues/api'

const emit = defineEmits<{ failed: [error: unknown] }>()

const { t } = useI18n()
const store = useIssueStore()

const children = ref<IssueRow[]>([])
const isAdding = ref(false)
const title = ref('')
const isSaving = ref(false)

const issueId = computed(() => store.issue?.id ?? '')

/** Тип подзадачи зависит от того, чей это ребёнок: у эпика — задача */
const childType = computed(() =>
  store.issue?.type === 'ISSUE_TYPE_EPIC' ? 'ISSUE_TYPE_TASK' : 'ISSUE_TYPE_SUBTASK',
)

const done = computed(
  () =>
    children.value.filter(
      (child) =>
        store.columns.find((column) => column.id === child.columnId)?.statusCategory ===
        'STATUS_CATEGORY_DONE',
    ).length,
)

const progress = computed(() =>
  children.value.length ? Math.round((done.value / children.value.length) * 100) : 0,
)

const load = async () => {
  if (!issueId.value || !store.issue) return

  try {
    children.value = await listChildren(store.issue.projectId, issueId.value)
  } catch (failure) {
    emit('failed', failure)
  }
}

const add = async () => {
  const name = title.value.trim()

  if (!name || !store.issue) return

  isSaving.value = true

  try {
    await createIssue({
      projectId: store.issue.projectId,
      title: name,
      descriptionMd: '',
      type: childType.value,
      priority: 'PRIORITY_MEDIUM',
      parentId: issueId.value,
    })

    title.value = ''
    isAdding.value = false
    await load()
  } catch (failure) {
    emit('failed', failure)
  } finally {
    isSaving.value = false
  }
}

/** Точка красится цветом колонки — тем же, что в пилюле статуса и на доске */
const dotColor = (child: IssueRow) =>
  store.columns.find((column) => column.id === child.columnId)?.color || 'var(--color-text-muted)'

onMounted(load)

watch(issueId, load)
</script>

<template>
  <section v-if="children.length || isAdding" class="isub">
    <header class="isub__head">
      <h2 class="isub__title">{{ t('issue.subtasks.title') }}</h2>
      <span class="isub__count">{{ done }} / {{ children.length }}</span>
    </header>

    <div class="isub__bar">
      <span class="isub__fill" :style="{ width: `${progress}%` }" />
    </div>

    <RouterLink
      v-for="child in children"
      :key="child.id"
      class="isub__row"
      :to="`/issue/${child.key}`"
    >
      <IssueTypeIcon :type="child.type" />
      <span class="isub__key">{{ child.key }}</span>
      <span class="isub__name">{{ child.title }}</span>
      <span class="isub__dot" :style="{ background: dotColor(child) }" />
    </RouterLink>

    <div v-if="isAdding" class="isub__add">
      <input
        v-model="title"
        class="isub__field"
        type="text"
        :placeholder="t('issue.subtasks.placeholder')"
        :aria-label="t('issue.subtasks.placeholder')"
        @keydown.enter="add"
        @keydown.esc="isAdding = false"
      />
      <AppButton variant="primary" size="small" :loading="isSaving" @click="add">
        {{ t('issues.create.submit') }}
      </AppButton>
    </div>

    <button v-else type="button" class="isub__new" @click="isAdding = true">
      <Plus :size="14" />
      {{ t('issue.subtasks.add') }}
    </button>
  </section>

  <button v-else type="button" class="isub__new isub__new--alone" @click="isAdding = true">
    <Plus :size="14" />
    {{ t('issue.subtasks.add') }}
  </button>
</template>

<style scoped>
.isub {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.isub__head {
  display: flex;
  align-items: center;
  gap: 8px;
}

.isub__title {
  margin: 0;
  color: var(--color-text-primary);
  font-size: var(--font-size-body-sm);
  font-weight: var(--font-weight-semibold);
  line-height: 17px;
}

.isub__count {
  color: var(--color-text-muted);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-mono);
}

/* Полоса 4px во всю ширину — как в макете: доля сделанного видна раньше, чем
   прочитан счётчик. */
.isub__bar {
  height: 4px;
  border-radius: var(--radius-full);
  background: var(--color-bg-muted);
  overflow: hidden;
}

.isub__fill {
  display: block;
  height: 100%;
  background: var(--color-success);
  transition: width 0.2s ease;
}

.isub__row {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 29px;
  color: inherit;
  text-decoration: none;
}

.isub__key {
  color: var(--color-text-muted);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-mono);
  line-height: 16px;
}

.isub__name {
  flex: 1;
  overflow: hidden;
  color: var(--color-text-primary);
  font-size: var(--font-size-body-sm);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.isub__row:hover .isub__name {
  text-decoration: underline;
}

.isub__dot {
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  border-radius: var(--radius-full);
}

.isub__add {
  display: flex;
  gap: 8px;
}

.isub__field {
  flex: 1;
  height: 32px;
  padding: 0 10px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-surface);
  color: var(--color-text-primary);
  font-family: var(--font-family-sans);
  font-size: var(--font-size-body-sm);
}

.isub__new {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  align-self: flex-start;
  padding: 0;
  border: none;
  background: none;
  color: var(--color-text-muted);
  font-family: var(--font-family-sans);
  font-size: var(--font-size-body-sm);
  cursor: pointer;
}

.isub__new:hover {
  color: var(--color-primary);
}

/* Когда подзадач нет, кнопка стоит одна — без заголовка и пустой полосы */
.isub__new--alone {
  margin-top: -4px;
}
</style>
